import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, TrendingUp, X, DollarSign, FileText, Plus } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, LineChart, Line } from 'recharts';

const ClientDashboardHome = ({ user, orders, onCreateService, onFilterServices, onSelectService }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'es' ? 'es-ES' : 'en-GB';

  // Get current week's orders (Monday to Sunday)
  const getCurrentWeekOrders = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to get Monday
    
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return orders.filter(order => {
      const orderDate = new Date(order.created_at);
      return orderDate >= monday && orderDate <= sunday;
    });
  };

  const weekOrders = getCurrentWeekOrders();

  // Calculate statistics for cards (all time)
  const stats = {
    total: orders.length,
    in_progress: orders.filter(o => 
      o.status === 'assigned' || 
      o.status === 'por_asignar' || 
      o.status === 'pending' || 
      o.status === 'confirmed' || 
      o.status === 'in_transit'
    ).length,
    completed: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => 
      o.status === 'cancelled' || 
      o.status?.includes('Cancelado')
    ).length,
    pending_payment: orders.filter(o => o.payment_status === 'pending').length,
    total_spent: orders.reduce((sum, o) => sum + (o.total_price || 0), 0),
    by_type: {
      delivery: orders.filter(o => o.service_type === 'delivery').length,
      bc_service: orders.filter(o => o.service_type === 'bc_service').length,
      tradein: orders.filter(o => o.service_type === 'tradein').length,
      representacion: orders.filter(o => o.service_type === 'representacion').length
    }
  };

  // Calculate statistics for charts (current week only)
  const weekStats = {
    in_progress: weekOrders.filter(o => 
      o.status === 'assigned' || 
      o.status === 'por_asignar' || 
      o.status === 'pending' || 
      o.status === 'confirmed' || 
      o.status === 'in_transit'
    ).length,
    completed: weekOrders.filter(o => o.status === 'delivered').length,
    cancelled: weekOrders.filter(o => 
      o.status === 'cancelled' || 
      o.status?.includes('Cancelado')
    ).length,
    by_type: {
      delivery: weekOrders.filter(o => o.service_type === 'delivery').length,
      bc_service: weekOrders.filter(o => o.service_type === 'bc_service').length,
      tradein: weekOrders.filter(o => o.service_type === 'tradein').length,
      representacion: weekOrders.filter(o => o.service_type === 'representacion').length
    }
  };

  // Chart data for status distribution - Show all statuses even if 0 (CURRENT WEEK)
  const statusChartData = [
    { name: t('in_progress'), value: weekStats.in_progress, color: '#3b82f6', status: 'in_progress' },
    { name: t('completed'), value: weekStats.completed, color: '#36e961', status: 'completed' },
    { name: t('cancelled'), value: weekStats.cancelled, color: '#ef4444', status: 'cancelled' }
  ];

  // Handler for pie chart click
  const handlePieClick = (data) => {
    if (data && data.status) {
      onFilterServices(data.status);
    }
  };

  // Prepare monthly trend data (last 12 months)
  const getMonthlyTrendData = () => {
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Generate last 12 months
    const monthlyData = [];
    for (let i = 11; i >= 0; i--) {
      const targetMonth = currentMonth - i;
      const targetYear = currentYear + Math.floor(targetMonth / 12);
      const adjustedMonth = ((targetMonth % 12) + 12) % 12;
      
      const monthOrders = orders.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate.getMonth() === adjustedMonth && orderDate.getFullYear() === targetYear;
      });
      
      const completed = monthOrders.filter(o => o.status === 'delivered').length;
      const cancelled = monthOrders.filter(o => 
        o.status === 'cancelled' || o.status?.includes('Cancelado')
      ).length;
      
      monthlyData.push({
        month: `${monthNames[adjustedMonth]} ${targetYear.toString().slice(-2)}`,
        completed: completed,
        cancelled: cancelled,
        total: monthOrders.length
      });
    }
    
    return monthlyData;
  };

  const monthlyTrendData = getMonthlyTrendData();

  // Chart data for service types - Show all types even if 0 (CURRENT WEEK)
  const serviceTypeChartData = [
    { name: t('delivery'), value: weekStats.by_type.delivery, type: 'delivery' },
    { name: t('bc_service'), value: weekStats.by_type.bc_service, type: 'bc_service' },
    { name: t('tradein'), value: weekStats.by_type.tradein, type: 'tradein' },
    { name: t('representacion'), value: weekStats.by_type.representacion, type: 'representacion' }
  ];

  // Handler for bar chart click
  const handleBarClick = (data) => {
    if (data && data.type) {
      onFilterServices('service_type', data.type);
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-[#0a3042] to-[#36e961] text-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold">¡Bienvenido, {user?.name?.split(' ')[0]}!</h2>
        <p className="text-sm mt-1 opacity-90">
          {t('member_since')} {new Date(user?.created_at || Date.now()).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card 
          className="hover:shadow-xl transition-all cursor-pointer hover:scale-105 transform hover:border-gray-400 border-2"
          onClick={() => onFilterServices('all')}
        >
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-gray-600">
              <Package className="w-4 h-4" />
              {t('total_services')}
            </CardDescription>
            <CardTitle className="text-3xl font-bold text-gray-900">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        
        <Card 
          className="hover:shadow-xl transition-all cursor-pointer hover:scale-105 transform hover:border-blue-400 border-2"
          onClick={() => onFilterServices('in_progress')}
        >
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              {t('in_progress')}
            </CardDescription>
            <CardTitle className="text-3xl font-bold text-blue-600">{stats.in_progress}</CardTitle>
          </CardHeader>
        </Card>
        
        <Card 
          className="hover:shadow-xl transition-all cursor-pointer hover:scale-105 transform hover:border-green-400 border-2"
          onClick={() => onFilterServices('completed')}
        >
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Package className="w-4 h-4" style={{color: '#36e961'}} />
              {t('completed')}
            </CardDescription>
            <CardTitle className="text-3xl font-bold" style={{color: '#36e961'}}>{stats.completed}</CardTitle>
          </CardHeader>
        </Card>
        
        <Card 
          className="hover:shadow-xl transition-all cursor-pointer hover:scale-105 transform hover:border-red-400 border-2"
          onClick={() => onFilterServices('cancelled')}
        >
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-red-600">
              <X className="w-4 h-4" />
              {t('cancelled')}
            </CardDescription>
            <CardTitle className="text-3xl font-bold text-red-500">{stats.cancelled}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Status Distribution Chart */}
        {orders.length > 0 && (
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">{t('services_by_status')}</CardTitle>
              <CardDescription>Servicios de esta semana (Lun - Dom) • Click para filtrar</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="45%"
                    labelLine={false}
                    label={({ value }) => value > 0 ? `${value}` : ''}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                    onClick={handlePieClick}
                    cursor="pointer"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} servicios`, name]} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value, entry) => `${value}: ${entry.payload.value}`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Service Type Distribution Chart */}
        {orders.length > 0 && (
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">{t('services_by_type')}</CardTitle>
              <CardDescription>Servicios de esta semana (Lun - Dom) • Click para filtrar</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={serviceTypeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{fontSize: 12}} />
                  <YAxis />
                  <Tooltip cursor={{fill: 'rgba(54, 233, 97, 0.1)'}} />
                  <Bar 
                    dataKey="value" 
                    fill="#36e961" 
                    radius={[8, 8, 0, 0]}
                    onClick={handleBarClick}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Monthly Trend Chart - Completados vs Cancelados */}
      {orders.length > 0 && (
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg">Evolución Temporal de Servicios</CardTitle>
            <CardDescription>Servicios completados vs cancelados en los últimos 12 meses</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart 
                data={monthlyTrendData}
                margin={{ top: 5, right: 30, left: 20, bottom: 70 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tick={{fontSize: 11}}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                />
                <YAxis 
                  allowDecimals={false}
                  label={{ 
                    value: 'Servicios', 
                    angle: -90, 
                    position: 'insideLeft', 
                    offset: 10,
                    style: { fontSize: 13, fontWeight: 500 } 
                  }}
                />
                <Tooltip 
                  formatter={(value, name) => [
                    value, 
                    name === 'completed' ? 'Completados' : name === 'cancelled' ? 'Cancelados' : 'Total'
                  ]}
                  labelFormatter={(label) => `Mes: ${label}`}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '10px' }}
                  formatter={(value) => {
                    if (value === 'completed') return 'Completados';
                    if (value === 'cancelled') return 'Cancelados';
                    if (value === 'total') return 'Total';
                    return value;
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="completed" 
                  stroke="#36e961" 
                  strokeWidth={3}
                  dot={{ fill: '#36e961', r: 5 }}
                  activeDot={{ r: 8 }}
                  name="completed"
                />
                <Line 
                  type="monotone" 
                  dataKey="cancelled" 
                  stroke="#ef4444" 
                  strokeWidth={3}
                  dot={{ fill: '#ef4444', r: 5 }}
                  activeDot={{ r: 8 }}
                  name="cancelled"
                />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#3b82f6', r: 4 }}
                  name="total"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Financial Summary */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" style={{color: '#36e961'}} />
            {t('financial_summary')}
          </CardTitle>
          <CardDescription>{t('client_financial_summary_desc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-gray-700 font-medium">{t('total_spent')}</p>
              <p className="text-3xl font-bold mt-1" style={{color: '#36e961'}}>€{stats.total_spent.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-gray-700 font-medium">{t('pending_payment')}</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{stats.pending_payment}</p>
              <p className="text-xs text-gray-600 mt-1">servicios pendientes</p>
            </div>
            <div className="flex items-center justify-center">
              <Button
                variant="outline"
                className="w-full h-full"
                onClick={() => {/* TODO: Navigate to invoices */}}
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('view_invoices')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Services */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader>
          <CardTitle>{t('recent_services')}</CardTitle>
          <CardDescription>{t('client_last_5_services')}</CardDescription>
        </CardHeader>
        <CardContent>
          {orders.slice(0, 5).length > 0 ? (
            <div className="space-y-3">
              {orders.slice(0, 5).map(order => {
                // Parse pickup and delivery times
                // Support multiple formats:
                // Format 1: "dd/MM/yyyy - dd/MM/yyyy | HH:mm - HH:mm"
                // Format 2: "HH:mm - HH:mm" (old format without dates)
                const parseTime = (marginTime) => {
                  if (!marginTime) return null;
                  try {
                    // Check if it contains a pipe (new format with dates)
                    if (marginTime.includes('|')) {
                      const parts = marginTime.split('|');
                      if (parts.length >= 2) {
                        const timePart = parts[1].trim();
                        const timeMatch = timePart.match(/(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/);
                        if (timeMatch) {
                          return `${timeMatch[1]}-${timeMatch[2]}`;
                        }
                      }
                    } else {
                      // Old format: just "HH:mm - HH:mm"
                      const timeMatch = marginTime.match(/(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/);
                      if (timeMatch) {
                        return `${timeMatch[1]}-${timeMatch[2]}`;
                      }
                    }
                  } catch (error) {
                    console.error('Error parsing time:', error);
                  }
                  return null;
                };

                const pickupTime = parseTime(order.pickup_margin_time);
                const deliveryTime = parseTime(order.delivery_margin_time);
                
                // DEBUG: Log for order e32684f6
                if (order.id && order.id.startsWith('e32684f6')) {
                  console.log('=== DEBUG Order e32684f6 ===');
                  console.log('pickup_margin_time:', order.pickup_margin_time);
                  console.log('delivery_margin_time:', order.delivery_margin_time);
                  console.log('Parsed pickupTime:', pickupTime);
                  console.log('Parsed deliveryTime:', deliveryTime);
                }

                return (
                  <div 
                    key={order.id} 
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition cursor-pointer border border-gray-200 hover:border-[#36e961]"
                    onClick={() => onSelectService && onSelectService(order.id)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className={'w-3 h-3 rounded-full ' + 
                        (order.status === 'delivered' ? 'bg-green-500' : 
                         order.status === 'cancelled' ? 'bg-red-500' : 
                         'bg-blue-500')}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-sm text-gray-900">#{order.id.substring(0, 8).toUpperCase()}</p>
                          <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{backgroundColor: '#36e961', color: 'white'}}>
                            {t(order.service_type)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">{order.car_plate}</p>
                        <div className="flex flex-col gap-1 mt-1">
                          <span className="text-xs text-gray-500">
                            📤 {t('client_pickup_label')} {pickupTime || 'N/A'}
                          </span>
                          <span className="text-xs text-gray-500">
                            📥 {t('client_delivery_label')} {deliveryTime || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm" style={{color: '#36e961'}}>€{(order.total_price || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-600">
                        {new Date(order.created_at).toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500 font-medium">{t('client_no_recent_services')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('client_create_first_service')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Action Button */}
      <div className="text-center py-4">
        <Button
          size="lg"
          className="text-white shadow-lg hover:shadow-xl transition-shadow"
          style={{backgroundColor: '#36e961'}}
          onClick={onCreateService}
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('create_order')}
        </Button>
      </div>
    </div>
  );
};

export default ClientDashboardHome;
